import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../redux/store/hooks';
import { switchAccount } from '../../redux/features/authSlice';
import { useDebounce } from '../../hooks/useDebounce';
import {
  useDoctors,
  useUpdateDoctor,
  useDeleteDoctor,
  useBulkDeleteDoctors,
  useDepartments,
  useDesignations,
  useSpecializations,
} from '../../services/httpServices/queries';
import { Badge } from '../../components/ui/badge';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { toast } from 'sonner';
import {
  Stethoscope,
  Plus,
  Loader2,
  AlertCircle,
  Search,
  X,
  Eye,
  Edit3,
  ToggleLeft,
  ToggleRight,
  SlidersHorizontal,
  Trash2,
  LogIn,
  Globe,
} from 'lucide-react';

interface ConfirmModalState {
  open: boolean;
  doctorId: string | null;
  doctorName: string;
  nextStatus: boolean;
}

interface DeleteRequestState {
  ids: string[];
  label: string;
}

function getInitials(name?: string) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  const f = parts[0]?.charAt(0) || '';
  const l = parts[1]?.charAt(0) || '';
  return (f + l).toUpperCase() || parts[0]?.charAt(0).toUpperCase() || '?';
}

function getAvatarColor(name: string) {
  const colors = [
    'bg-teal-100 text-teal-700',
    'bg-blue-100 text-blue-700',
    'bg-indigo-100 text-indigo-700',
    'bg-rose-100 text-rose-700',
    'bg-amber-100 text-amber-700',
    'bg-emerald-100 text-emerald-700',
    'bg-cyan-100 text-cyan-700',
    'bg-violet-100 text-violet-700',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

export function Doctors() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const isSuperAdmin = !!user?.roles?.includes('super_admin');
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  const limit = 20;

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [page, setPage] = useState(() => {
    const p = params.get('page');
    return p ? Math.max(1, parseInt(p, 10)) : 1;
  });

  const [search, setSearch] = useState(() => params.get('search') || '');
  const debouncedSearch = useDebounce(search, 350);
  const [departmentId, setDepartmentId] = useState(() => params.get('departmentId') || '');
  const [designationId, setDesignationId] = useState(() => params.get('designationId') || '');
  const [specializationId, setSpecializationId] = useState(() => params.get('specializationId') || '');
  const [status, setStatus] = useState(() => params.get('status') || '');
  const [showFilters, setShowFilters] = useState(false);

  const filters = {
    search: debouncedSearch || undefined,
    departmentId: departmentId || undefined,
    designationId: designationId || undefined,
    specializationId: specializationId || undefined,
    status: status || undefined,
  };

  const { data, isLoading, error } = useDoctors(page, limit, filters);
  const updateDoctor = useUpdateDoctor();
  const deleteDoctor = useDeleteDoctor();
  const bulkDeleteDoctor = useBulkDeleteDoctors();

  const { data: departmentsData } = useDepartments();
  const { data: designationsData } = useDesignations();
  const { data: specializationsData } = useSpecializations();

  const departments = departmentsData?.data || [];
  const designations = designationsData?.data || [];
  const specializations = specializationsData?.data || [];

  useEffect(() => {
    const newParams = new URLSearchParams();
    if (page > 1) newParams.set('page', String(page));
    if (debouncedSearch) newParams.set('search', debouncedSearch);
    if (departmentId) newParams.set('departmentId', departmentId);
    if (designationId) newParams.set('designationId', designationId);
    if (specializationId) newParams.set('specializationId', specializationId);
    if (status) newParams.set('status', status);

    const query = newParams.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ''}`, { replace: true });
  }, [page, debouncedSearch, departmentId, designationId, specializationId, status, navigate, location.pathname]);

  useEffect(() => {
    setPage(1);
  }, [search, departmentId, designationId, specializationId, status]);

  const [confirm, setConfirm] = useState<ConfirmModalState>({
    open: false,
    doctorId: null,
    doctorName: '',
    nextStatus: false,
  });

  const [deleteRequest, setDeleteRequest] = useState<DeleteRequestState | null>(null);

  const doctors = useMemo(() => data?.data || [], [data]);
  const meta = data?.meta;
  const trashedCount = Number(data?.stats?.trashed ?? 0);

  // Row selection for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  // Drop selected ids that are no longer visible (e.g. after paging/filtering).
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(doctors.map((d: any) => d.id));
      const next = new Set<string>();
      let changed = false;
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [doctors]);

  const allSelected = doctors.length > 0 && doctors.every((d: any) => selectedIds.has(d.id));
  const someSelected = doctors.some((d: any) => selectedIds.has(d.id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const everySelected = doctors.length > 0 && doctors.every((d: any) => prev.has(d.id));
      return everySelected ? new Set() : new Set(doctors.map((d: any) => d.id));
    });
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const requestDeleteOne = (doctor: any) => {
    setDeleteRequest({ ids: [doctor.id], label: doctor.name || 'this doctor' });
  };

  const requestDeleteSelected = () => {
    setDeleteRequest({ ids: Array.from(selectedIds), label: '' });
  };

  const handleConfirmDelete = async () => {
    if (!deleteRequest || deleteRequest.ids.length === 0) return;
    const { ids } = deleteRequest;
    try {
      if (ids.length === 1) {
        await deleteDoctor.mutateAsync(ids[0]);
      } else {
        await bulkDeleteDoctor.mutateAsync(ids);
      }
      setSelectedIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
      toast.success(`Moved ${ids.length} doctor${ids.length === 1 ? '' : 's'} to Trash`);
    } catch {
      toast.error('Failed to delete doctor(s)');
    } finally {
      setDeleteRequest(null);
    }
  };

  const openToggleConfirm = (doctor: any) => {
    setConfirm({
      open: true,
      doctorId: doctor.id,
      doctorName: doctor.name,
      nextStatus: !doctor.isActive,
    });
  };

  const closeConfirm = () => {
    setConfirm({ open: false, doctorId: null, doctorName: '', nextStatus: false });
  };

  const handleToggleStatus = async () => {
    if (!confirm.doctorId) return;
    try {
      await updateDoctor.mutateAsync({
        id: confirm.doctorId,
        data: { isActive: confirm.nextStatus },
      });
      closeConfirm();
    } catch {
      // mutation error handled globally
    }
  };

  const handleSwitchAccount = async (doctor: any) => {
    const linkUserId = doctor.userId || doctor.user?.id;
    if (!linkUserId) {
      toast.error('This doctor has no linked user account to switch to.');
      return;
    }
    try {
      setSwitchingId(doctor.id);
      await dispatch(switchAccount(linkUserId)).unwrap();
      toast.success(`Switched to ${doctor.name || 'doctor'}`);
      // Reload so all cached data is rebuilt for the new identity.
      window.location.assign('/');
    } catch (error: any) {
      toast.error(typeof error === 'string' ? error : 'Failed to switch account');
      setSwitchingId(null);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setDepartmentId('');
    setDesignationId('');
    setSpecializationId('');
    setStatus('');
    setPage(1);
  };

  const hasActiveFilters = search || departmentId || designationId || specializationId || status;
  const deletePending = deleteDoctor.isPending || bulkDeleteDoctor.isPending;

  return (
    <div className="space-y-5">
      {/* Confirmation Modal */}
      {confirm.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-surface rounded-xl border border-border p-6 max-w-sm w-full space-y-4">
            <h3 className="text-lg font-semibold text-text">Confirm Status Change</h3>
            <p className="text-sm text-text-muted">
              Are you sure you want to <strong>{confirm.nextStatus ? 'activate' : 'deactivate'}</strong>{' '}
              <strong>{confirm.doctorName}</strong>?
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={closeConfirm}
                className="px-4 py-2 text-text-muted hover:text-text transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleToggleStatus}
                disabled={updateDoctor.isPending}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2 ${
                  confirm.nextStatus
                    ? 'bg-success text-white hover:bg-success-dark'
                    : 'bg-danger text-white hover:bg-danger-dark'
                }`}
              >
                {updateDoctor.isPending && <Loader2 size={16} className="animate-spin" />}
                {confirm.nextStatus ? 'Activate' : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Stethoscope className="w-5 h-5 text-primary" />
          <h1 className="text-xl font-semibold text-text">Doctors</h1>
          {meta && (
            <Badge variant="default" className="ml-1">
              {meta.total}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/doctors/trash')}
            className="px-3 py-2 border border-border text-text-muted hover:text-danger hover:border-danger rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
            title="View trash"
          >
            <Trash2 size={16} />
            Trash
            {trashedCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-danger text-white text-[10px] font-semibold leading-none">
                {trashedCount > 99 ? '99+' : trashedCount}
              </span>
            )}
          </button>
          <button
            onClick={() => navigate('/doctors/new')}
            className="px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
          >
            <Plus size={16} />
            Add Doctor
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-surface rounded-xl border border-border p-3 space-y-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Search by name, doctor code, email, or phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters((s) => !s)}
              className={`px-3 py-2 border rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                showFilters || hasActiveFilters
                  ? 'border-primary text-primary bg-primary/5'
                  : 'border-border text-text-muted hover:text-text hover:bg-background'
              }`}
            >
              <SlidersHorizontal size={16} />
              Filters
              {hasActiveFilters && (
                <span className="w-2 h-2 rounded-full bg-primary" />
              )}
            </button>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5"
              >
                <X size={14} />
                Clear
              </button>
            )}
          </div>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 pt-1 border-t border-border">
            <select
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
              className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="">All Departments</option>
              {departments.map((d: any) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
            <select
              value={designationId}
              onChange={(e) => setDesignationId(e.target.value)}
              className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="">All Designations</option>
              {designations.map((d: any) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
            <select
              value={specializationId}
              onChange={(e) => setSpecializationId(e.target.value)}
              className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="">All Specializations</option>
              {specializations.map((s: any) => (
                <option key={s.id} value={s.id}>{s.label || s.name}</option>
              ))}
            </select>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        )}
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.size}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={requestDeleteSelected}
              disabled={deletePending}
              className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              {bulkDeleteDoctor.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Delete
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              disabled={deletePending}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-danger/10 border border-danger/20 rounded-lg text-danger text-sm">
          <AlertCircle size={16} />
          <span>Failed to load doctors. Please try again.</span>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      )}

      {/* Table */}
      {!isLoading && (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-4 py-2.5 w-10 text-left">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={doctors.length === 0}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                      title="Select all"
                    />
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Doctor</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Position</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Departments</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Room</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Status</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-text-muted uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {doctors.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-text-muted text-sm">
                      {hasActiveFilters ? (
                        <span>No doctors match your filters.{' '}
                          <button onClick={clearFilters} className="text-primary hover:underline font-medium">
                            Clear filters
                          </button>
                        </span>
                      ) : (
                        <span>No doctors found.{' '}
                          <button
                            onClick={() => navigate('/doctors/new')}
                            className="text-primary hover:underline font-medium"
                          >
                            Register a doctor
                          </button>
                        </span>
                      )}
                    </td>
                  </tr>
                ) : (
                  doctors.map((doctor: any) => {
                    const name = doctor.name || 'Unknown';
                    const initials = getInitials(name);
                    const avatarClass = getAvatarColor(name);
                    const linkUserId = doctor.userId || doctor.user?.id;
                    const doctorDepartments = doctor.departments && doctor.departments.length > 0
                      ? doctor.departments
                      : (doctor.department ? [doctor.department] : []);
                    return (
                      <tr key={doctor.id} className="hover:bg-background/50 transition-colors">
                        <td className="px-4 py-2.5">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(doctor.id)}
                            onChange={() => toggleOne(doctor.id)}
                            className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                          />
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${avatarClass}`}>
                              {initials}
                            </div>
                            <div className="min-w-0">
                              <button
                                onClick={() => navigate(`/doctors/${doctor.slug || doctor.id}`)}
                                className="block text-sm font-medium text-text hover:text-primary transition-colors text-left truncate"
                                title={name}
                              >
                                {name}
                              </button>
                              <span className="text-xs text-text-muted font-mono">
                                {doctor.employeeCode || '—'}
                              </span>
                              {doctor.showOnWebsite && (
                                <span
                                  className="inline-flex items-center gap-1 text-[10px] font-medium text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded mt-0.5"
                                  title="Visible on the public website"
                                >
                                  <Globe size={10} />
                                  Website
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="text-sm text-text">
                            {doctor.designation?.name || '—'}
                          </div>
                          {doctor.specializations && doctor.specializations.length > 0 && (
                            <div className="text-xs text-text-muted">
                              {doctor.specializations.map((s: any) => s.label).join(', ')}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          {doctorDepartments.length === 0 ? (
                            <span className="text-sm text-text-muted">—</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {doctorDepartments.map((dept: any, idx: number) => (
                                <Badge
                                  key={dept.id || `${dept.name}-${idx}`}
                                  variant="default"
                                  title={dept.code ? `${dept.name} (${dept.code})` : dept.name}
                                >
                                  {dept.name}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-sm text-text-muted">
                          {doctor.consultationRoom?.name || '—'}
                        </td>
                        <td className="px-4 py-2.5">
                          <Badge variant={doctor.isActive ? 'success' : 'danger'}>
                            {doctor.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center justify-end gap-0.5">
                            <button
                              onClick={() => navigate(`/doctors/${doctor.slug || doctor.id}`)}
                              className="p-1.5 text-text-muted hover:text-primary hover:bg-primary/10 rounded-md transition-colors"
                              title="View"
                            >
                              <Eye size={15} />
                            </button>
                            <button
                              onClick={() => navigate(`/doctors/${doctor.slug || doctor.id}/edit`)}
                              className="p-1.5 text-text-muted hover:text-primary hover:bg-primary/10 rounded-md transition-colors"
                              title="Edit"
                            >
                              <Edit3 size={15} />
                            </button>
                            {isSuperAdmin && !!linkUserId && (
                              <button
                                onClick={() => handleSwitchAccount(doctor)}
                                disabled={switchingId === doctor.id}
                                className="p-1.5 text-text-muted hover:text-primary hover:bg-primary/10 rounded-md transition-colors disabled:opacity-50"
                                title={`Switch to ${name}'s account`}
                              >
                                {switchingId === doctor.id ? (
                                  <Loader2 size={15} className="animate-spin" />
                                ) : (
                                  <LogIn size={15} />
                                )}
                              </button>
                            )}
                            <button
                              onClick={() => openToggleConfirm(doctor)}
                              className={`p-1.5 rounded-md transition-colors ${
                                doctor.isActive
                                  ? 'text-success hover:bg-success/10'
                                  : 'text-danger hover:bg-danger/10'
                              }`}
                              title={doctor.isActive ? 'Deactivate' : 'Activate'}
                            >
                              {doctor.isActive ? <ToggleRight size={15} /> : <ToggleLeft size={15} />}
                            </button>
                            <button
                              onClick={() => requestDeleteOne(doctor)}
                              className="p-1.5 text-text-muted hover:text-danger hover:bg-danger/10 rounded-md transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <Pagination
            page={page}
            totalPages={meta?.totalPages || 1}
            total={meta?.total || 0}
            limit={limit}
            onPageChange={setPage}
            itemName="doctors"
          />
        </div>
      )}

      <ConfirmDialog
        open={!!deleteRequest}
        title={
          deleteRequest && deleteRequest.ids.length > 1
            ? `Delete ${deleteRequest.ids.length} doctors?`
            : `Delete "${deleteRequest?.label}"?`
        }
        description="This will move the selected record to the Trash. You can restore it later."
        confirmLabel={
          deleteRequest && deleteRequest.ids.length > 1
            ? `Delete ${deleteRequest.ids.length} doctors`
            : 'Delete'
        }
        loading={deletePending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteRequest(null)}
      />
    </div>
  );
}
